import { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ClearPEPayment from './components/ClearPEPayment';
import ManualDeposit from './components/ManualDeposit';
import { fetchAmountDetails, checkPaymentStatus, clearPendingOrderFromLocalStorage } from './services/paymentService';
import { WalletContext } from './WalletContext';
import { useToast } from './hooks/useToast';
import './AddFunds.css';

function AddFunds({ onBack }) {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [userId, setUserId] = useState(null);
  const [minimumDeposit, setMinimumDeposit] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('auto');
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const { walletBalance, updateWalletBalance } = useContext(WalletContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    let resolvedUserId = null;

    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        resolvedUserId = user.id;
        setUserId(user.id);
      }
    } catch (error) {
      console.error('Error reading user data from localStorage:', error);
    }

    const orderId = searchParams.get('order_id');
    if (orderId) {
      // Strip order_id from URL immediately so refresh doesn't re-trigger this
      navigate('/addfunds', { replace: true });

      checkPaymentStatus(orderId)
        .then((data) => {
          if (data.success) {
            toast.success('Funds added successfully!');
            if (resolvedUserId) {
              updateWalletBalance(resolvedUserId);
            }
          } else {
            toast.error('Payment failed. Please try again.');
          }
          clearPendingOrderFromLocalStorage();
        })
        .catch(() => {
          toast.error('Could not verify payment. Please contact support.');
          clearPendingOrderFromLocalStorage();
        });
    } else {
      localStorage.removeItem('pendingPaymentOrder');
    }

    fetchAmountDetails()
      .then((data) => {
        setMinimumDeposit(data.minimum_deposit || 1);
        setPaymentMethod(data.payment_method || 'auto');
        setIsLoadingDetails(false);
      })
      .catch((error) => {
        console.error('Error fetching amount details:', error);
        setIsLoadingDetails(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaymentSuccess = (data) => {
    console.log('Payment successful:', data);
    // You can add additional logic here (e.g., fetch updated wallet balance)
    setTimeout(() => {
      setShowPaymentForm(false);
      onBack?.();
    }, 2000);
  };

  const handlePaymentFailed = (data) => {
    console.log('Payment failed:', data);
    // Retry logic can be added here
  };

  if (showPaymentForm) {
    return (
      <div className="add-funds-page">
        <div className="add-funds-header">
          <button className="back-btn" onClick={() => setShowPaymentForm(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <h1>Add Funds</h1>
        </div>
        <div className="add-funds-content">
          {isLoadingDetails ? (
            <div className="loading-message">Loading...</div>
          ) : userId ? (
            paymentMethod === 'auto' ? (
              <ClearPEPayment
                userId={userId}
                minimumDeposit={minimumDeposit}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentFailed={handlePaymentFailed}
              />
            ) : (
              <ManualDeposit
                userId={userId}
                minimumDeposit={minimumDeposit}
                onBack={() => setShowPaymentForm(false)}
              />
            )
          ) : (
            <div className="loading-message">Loading user data...</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="add-funds-page">
      {/* Header */}
      <div className="add-funds-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Add Funds</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </div>

      {/* Content */}
      <div className="add-funds-content">
        {/* Info Card */}
        <div className="info-card">
          <div className="info-text">MINIMUM DEPOSIT ₹{minimumDeposit}/-</div>
          <div className="info-text">FAST DEPOSIT</div>
          {/* <div className="info-text">5% bonus on deposit above 2000</div> */}
          <div className="info-text safe-text">SAFE AND SECURE PAYMENT FULL TRUSTED APPLICATION</div>
        </div>

        {/* Wallet Balance Card */}
        {/* <div className="wallet-card">
          <div className="wallet-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect x="8" y="12" width="28" height="20" rx="3" stroke="#1e3a5f" strokeWidth="2.5" fill="none"/>
              <path d="M12 12V10C12 8 13 6 18 6C23 6 24 8 24 10V12" stroke="#1e3a5f" strokeWidth="2.5"/>
              <circle cx="32" cy="22" r="4" fill="#1e3a5f"/>
              <circle cx="32" cy="22" r="2" fill="white"/>
              <path d="M28 18L32 22L36 18" stroke="#4a9b8e" strokeWidth="2" strokeLinecap="round" fill="none"/>
            </svg>
          </div>
          <div className="wallet-info">
            <div className="wallet-amount">₹ 5</div>
            <div className="wallet-label">Total Wallet Balance</div>
          </div>
        </div> */}

        {/* Add Fund Section */}
        <div className="add-fund-section">
          <h2 className="section-title">Payment Instructions</h2>

          <div className="info-box">
            <div className="instruction-item">
              <p>सबसे पहले Proceed To Make Payment पर click करें और amount add करें।</p>
            </div>
          </div>
          

          

          {/* Pay Now Button */}
          <button
            className="pay-now-btn"
            onClick={() => setShowPaymentForm(true)}
            disabled={isLoadingDetails}
          >
            {isLoadingDetails ? 'Loading...' : (paymentMethod === 'auto' ? 'Proceed To Make Payment' : 'Proceed To Make Payment')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddFunds;
